<template>
  <div class="asset-dashboard-wrapper">
    <!-- 背景装饰 -->
    <div class="dashboard-bg">
      <div class="bg-orb orb-1"></div>
      <div class="bg-orb orb-2"></div>
    </div>

    <!-- ===== 资产总览 ===== -->
    <div class="asset-overview">
      <div class="asset-overview-header">
        <div class="asset-overview-header-left">
          <span class="asset-overview-title">资产总览</span>
          <span class="asset-overview-sub">Asset Overview</span>
        </div>
        <div class="asset-overview-badge">
          <span class="badge-dot"></span>
          实时更新
        </div>
      </div>

      <div class="asset-overview-body">
        <div class="asset-total-card">
          <div class="asset-total-glow"></div>
          <div class="asset-total-icon">
            <svg
              viewBox="0 0 24 24"
              width="22"
              height="22"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <line x1="2" y1="10" x2="22" y2="10" />
            </svg>
          </div>
          <div class="asset-total-info">
            <div class="asset-total-label">总资产</div>
            <div class="asset-total-amount">
              <span class="currency-sign">$</span>
              <span class="amount-number">{{ wallet.total }}</span>
            </div>
          </div>
          <div class="asset-total-trend">
            <span
              :class="{
                'trend-up': wallet.change > 0,
                'trend-down': wallet.change < 0,
              }"
            >
              {{ wallet.change > 0 ? "↑" : "↓" }}
              {{ wallet.change }}%</span
            >
            <span class="trend-label">较昨日</span>
          </div>
        </div>

        <div class="asset-sub-cards">
          <div class="asset-sub-card">
            <div class="sub-card-icon sub-card-icon-balance">
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v12" />
                <path
                  d="M15 9.5c0-1.5-1.34-2.5-3-2.5s-3 1-3 2.5 1.34 2.5 3 2.5 3 1 3 2.5-1.34 2.5-3 2.5-3-1-3-2.5"
                />
              </svg>
            </div>
            <div class="sub-card-info">
              <div class="sub-card-label">可用余额</div>
              <div class="sub-card-value">${{ wallet.balance }}</div>
            </div>
          </div>

          <div class="asset-sub-card">
            <div class="sub-card-icon sub-card-icon-freeze">
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <div class="sub-card-info">
              <div class="sub-card-label">占用资金</div>
              <div class="sub-card-value">${{ wallet.freeze }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== 收益趋势 ===== -->
    <div class="chart-wrapper">
      <div class="chart-header">
        <div class="chart-header-left">
          <span class="chart-title">收益趋势</span>
          <!-- 时间切换按钮 -->
          <div class="time-tabs">
            <button
              v-for="item in timeOptions"
              :key="item.value"
              class="time-tab"
              :class="{ active: currentTimeRange === item.value }"
              @click="switchTimeRange(item.value)"
            >
              {{ item.label }}
            </button>
          </div>
        </div>
        <div class="chart-legend">
          <span class="legend-item">
            <span class="dot dot-hash"></span> 算力收益
          </span>
          <span class="legend-item">
            <span class="dot dot-lease"></span> 租赁收益
          </span>
        </div>
      </div>
      <div ref="chartRef" class="chart-container"></div>
    </div>

    <!-- ===== 收益总额 ===== -->
    <div class="total-stats-new">
      <div class="stat-card stat-card-hash">
        <div class="stat-card-left">
          <div class="stat-card-icon">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <rect x="2" y="7" width="20" height="14" rx="2" />
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
            </svg>
          </div>
          <div>
            <div class="stat-card-label">算力收益总额</div>
            <div class="stat-card-value">
              ${{ formatNumber(profit.token_income) }}
            </div>
          </div>
        </div>
        <div class="stat-card-right">
          <div class="stat-ring">
            <svg viewBox="0 0 50 50" width="50" height="50">
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="rgba(255,255,255,0.06)"
                stroke-width="4"
              />
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="#00b4ff"
                stroke-width="4"
                stroke-dasharray="125.6"
                stroke-dashoffset="31.4"
                stroke-linecap="round"
                transform="rotate(-90 25 25)"
              />
            </svg>
            <span class="stat-ring-label">{{
              formatRate(profit.token_day_rate)
            }}</span>
          </div>
        </div>
      </div>

      <div class="stat-card stat-card-lease">
        <div class="stat-card-left">
          <div class="stat-card-icon">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
          </div>
          <div>
            <div class="stat-card-label">租赁收益总额</div>
            <div class="stat-card-value">
              ${{ formatNumber(profit.server_income) }}
            </div>
          </div>
        </div>
        <div class="stat-card-right">
          <div class="stat-ring">
            <svg viewBox="0 0 50 50" width="50" height="50">
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="rgba(255,255,255,0.06)"
                stroke-width="4"
              />
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="#7c3aed"
                stroke-width="4"
                stroke-dasharray="125.6"
                stroke-dashoffset="62.8"
                stroke-linecap="round"
                transform="rotate(-90 25 25)"
              />
            </svg>
            <span class="stat-ring-label">{{
              formatRate(profit.server_day_rate)
            }}</span>
          </div>
        </div>
      </div>

      <div class="stat-card stat-card-total">
        <div class="stat-card-left">
          <div class="stat-card-icon">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
          </div>
          <div>
            <div class="stat-card-label">总收益</div>
            <div class="stat-card-value">${{ formatNumber(totalIncome) }}</div>
          </div>
        </div>
        <div class="stat-card-right">
          <span class="stat-total-badge">{{
            formatRate(profit.total_day_rate)
          }}</span>
          <span class="stat-total-label">较上月</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const echarts = window.echarts || require("echarts");

export default {
  name: "AssetDashboard",
  data() {
    return {
      currentTimeRange: "7d",
      timeOptions: [
        { label: "7天", value: "7d" },
        { label: "15天", value: "15d" },
        { label: "30天", value: "30d" },
      ],
      chartInstance: null,
      wallet: {},
      profit: {},
    };
  },

  computed: {
    // 总收益 = 算力收益 + 租赁收益
    totalIncome() {
      return (this.profit.server_income || 0) + (this.profit.token_income || 0);
    },
  },

  created() {
    this.getWallet();
    this.getProfit();
  },
  mounted() {
    this.$nextTick(() => {
      this.initChart();
    });
    window.addEventListener("resize", this.resizeChart);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeChart);
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
  },
  methods: {
    // 格式化数字为千分位格式
    formatNumber(value) {
      if (value === undefined || value === null || value === "") return "0.00";
      const num = Number(value);
      if (isNaN(num)) return "0.00";
      return num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    // 格式化收益率（如 0.5 -> +0.50%）
    formatRate(value) {
      if (value === undefined || value === null || value === "")
        return "+0.00%";
      const num = Number(value);
      if (isNaN(num)) return "+0.00%";
      const sign = num >= 0 ? "+" : "";
      return sign + num.toFixed(2) + "%";
    },

    getProfit() {
      this.$api.user.getProfit().then((res) => {
        this.profit = res.data;
        // 数据加载完成后更新图表
        if (this.chartInstance) {
          this.updateChart();
        }
      });
    },

    getWallet() {
      this.$api.userWallet.getWallet().then((res) => {
        this.wallet = res.data;
      });
    },
    // 获取当前时间范围的图表数据
    getChartData() {
      const rangeKey = this.currentTimeRange.replace("d", ""); // "7d" -> "7"
      const serverTrend = (this.profit.server_trend || {})[rangeKey] || [];
      const tokenTrend = (this.profit.token_trend || {})[rangeKey] || [];
      return {
        xData: tokenTrend.map((item) => item.date),
        hashData: tokenTrend.map((item) => item.amount),
        leaseData: serverTrend.map((item) => item.amount),
      };
    },

    switchTimeRange(value) {
      this.currentTimeRange = value;
      this.updateChart();
    },
    updateChart() {
      if (!this.chartInstance) return;
      const data = this.getChartData();
      this.chartInstance.setOption({
        xAxis: { data: data.xData },
        series: [{ data: data.hashData }, { data: data.leaseData }],
      });
    },
    initChart() {
      if (!this.$refs.chartRef) return;
      if (typeof echarts === "undefined") {
        console.warn("ECharts 未加载，请引入 ECharts");
        return;
      }

      this.chartInstance = echarts.init(this.$refs.chartRef, "dark");
      const data = this.getChartData();

      const option = {
        tooltip: {
          trigger: "axis",
          backgroundColor: "rgba(10, 14, 26, 0.92)",
          borderColor: "rgba(255,255,255,0.08)",
          borderWidth: 1,
          textStyle: { color: "#e0e6ed", fontSize: 12 },
          formatter: function (params) {
            let res = `<div style="font-weight:600;margin-bottom:4px;color:#fff;">${params[0].axisValue}</div>`;
            params.forEach((p) => {
              res += `<div style="display:flex;justify-content:space-between;gap:20px;padding:2px 0;">
                <span>${p.marker} ${p.seriesName}</span>
                <span style="font-weight:600;color:#fff;">$${p.value}</span>
              </div>`;
            });
            return res;
          },
        },
        legend: { show: false },
        grid: {
          left: "3%",
          right: "1.5%",
          bottom: "10%",
          top: "6%",
          containLabel: false,
        },
        xAxis: {
          type: "category",
          data: data.xData,
          axisLine: { lineStyle: { color: "rgba(255,255,255,0.06)" } },
          axisTick: { show: false },
          axisLabel: { color: "rgba(255,255,255,0.3)", fontSize: 11 },
          splitLine: { show: false },
          boundaryGap: false,
        },
        yAxis: {
          type: "value",
          splitLine: {
            lineStyle: { color: "rgba(255,255,255,0.04)", type: "dashed" },
          },
          axisLabel: {
            color: "rgba(255,255,255,0.3)",
            fontSize: 11,
            formatter: (val) => "$" + val,
          },
          axisLine: { show: false },
          axisTick: { show: false },
        },
        series: [
          {
            name: "算力收益",
            type: "line",
            smooth: true,
            symbol: "circle",
            symbolSize: 6,
            lineStyle: { color: "#00b4ff", width: 2.5 },
            areaStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: "rgba(0, 180, 255, 0.20)" },
                  { offset: 1, color: "rgba(0, 180, 255, 0.01)" },
                ],
              },
            },
            itemStyle: { color: "#00b4ff" },
            data: data.hashData,
          },
          {
            name: "租赁收益",
            type: "line",
            smooth: true,
            symbol: "diamond",
            symbolSize: 7,
            lineStyle: { color: "#7c3aed", width: 2.5 },
            areaStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: "rgba(124, 58, 237, 0.16)" },
                  { offset: 1, color: "rgba(124, 58, 237, 0.01)" },
                ],
              },
            },
            itemStyle: { color: "#7c3aed" },
            data: data.leaseData,
          },
        ],
      };

      this.chartInstance.setOption(option);
    },
    resizeChart() {
      if (this.chartInstance) {
        this.chartInstance.resize();
      }
    },
  },
};
</script>

<style scoped>
.asset-dashboard-wrapper {
  position: relative;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
}

.dashboard-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;
}

.bg-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.12;
}

.orb-1 {
  width: 300px;
  height: 300px;
  background: #0066ff;
  top: -80px;
  right: 20px;
}

.orb-2 {
  width: 240px;
  height: 240px;
  background: #7c3aed;
  bottom: -60px;
  left: -40px;
  opacity: 0.08;
}

/* ===== 资产总览 ===== */
.asset-overview {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 20px 24px;
  margin-bottom: 16px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;
  z-index: 1;
  overflow: hidden;
}

.asset-overview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.asset-overview-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.asset-overview-title {
  font-size: 15px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
}

.asset-overview-sub {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.25);
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.asset-overview-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(0, 201, 81, 0.8);
  background: rgba(0, 201, 81, 0.08);
  padding: 3px 12px;
  border-radius: 12px;
  border: 1px solid rgba(0, 201, 81, 0.12);
}

.badge-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #00c951;
  box-shadow: 0 0 8px rgba(0, 201, 81, 0.5);
  animation: badge-pulse 2s ease-in-out infinite;
}

@keyframes badge-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

.asset-overview-body {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  gap: 12px;
}

/* 总资产卡片 */
.asset-total-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-radius: 14px;
  background: linear-gradient(
    135deg,
    rgba(0, 180, 255, 0.08),
    rgba(124, 58, 237, 0.08)
  );
  border: 1px solid rgba(0, 180, 255, 0.12);
  overflow: hidden;
}

.asset-total-glow {
  position: absolute;
  top: -40px;
  right: -40px;
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 180, 255, 0.15), transparent 70%);
  pointer-events: none;
}

.asset-total-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgba(0, 180, 255, 0.15),
    rgba(124, 58, 237, 0.15)
  );
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.asset-total-info {
  flex: 1;
  min-width: 0;
}

.asset-total-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  font-weight: 400;
  letter-spacing: 0.3px;
  margin-bottom: 2px;
}

.asset-total-amount {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.asset-total-amount .currency-sign {
  font-size: 18px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.5);
}

.asset-total-amount .amount-number {
  font-size: 28px;
  font-weight: 800;
  color: #fff;
  font-family: "SF Mono", "Courier New", monospace;
  letter-spacing: 1px;
  background: linear-gradient(135deg, #00b4ff, #7c3aed);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.asset-total-trend {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
}
.trend-down {
  font-size: 13px;
  font-weight: 700;
  color: #ff9800;
  background: rgba(255, 152, 0, 0.12);
  padding: 2px 10px;
  border-radius: 12px;
}

.trend-up {
  font-size: 13px;
  font-weight: 700;
  color: #00c951;
  background: rgba(0, 201, 81, 0.12);
  padding: 2px 10px;
  border-radius: 12px;
}

.trend-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.25);
}

/* 子卡片 */
.asset-sub-cards {
  display: contents;
}

.asset-sub-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
}

.asset-sub-card:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
  transform: translateY(-2px);
}

.sub-card-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sub-card-icon-balance {
  background: rgba(0, 180, 255, 0.12);
  color: #00b4ff;
}

.sub-card-icon-freeze {
  background: rgba(255, 152, 0, 0.12);
  color: #ff9800;
}

.sub-card-info {
  min-width: 0;
}

.sub-card-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  font-weight: 400;
  letter-spacing: 0.3px;
  margin-bottom: 2px;
}

.sub-card-value {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  font-family: "SF Mono", "Courier New", monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ===== 图表区域 ===== */
.chart-wrapper {
  background: transparent;
  border-radius: 16px;
  padding: 16px 0 8px 0;
  margin-bottom: 16px;
  border: none;
  position: relative;
  z-index: 1;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding: 0 0 0 0;
}

.chart-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.chart-title {
  font-size: 15px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
}

/* ===== 时间切换按钮 ===== */
.time-tabs {
  display: flex;
  gap: 4px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  padding: 2px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.time-tab {
  padding: 4px 14px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: rgba(255, 255, 255, 0.35);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.time-tab:hover {
  color: rgba(255, 255, 255, 0.6);
}

.time-tab.active {
  background: rgba(0, 180, 255, 0.15);
  color: #00b4ff;
  box-shadow: 0 0 20px rgba(0, 180, 255, 0.05);
}

.chart-legend {
  display: flex;
  gap: 20px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.dot-hash {
  background: #00b4ff;
  box-shadow: 0 0 12px rgba(0, 180, 255, 0.3);
}

.dot-lease {
  background: #7c3aed;
  box-shadow: 0 0 12px rgba(124, 58, 237, 0.3);
}

.chart-container {
  width: 100%;
  height: 200px;
  min-height: 200px;
}

/* ===== 收益总额卡片 ===== */
.total-stats-new {
  display: grid;
  grid-template-columns: 1fr 1fr 1.2fr;
  gap: 12px;
  position: relative;
  z-index: 1;
}

.stat-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
  cursor: default;
}

.stat-card:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
  transform: translateY(-2px);
}

.stat-card-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.stat-card-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-card-hash .stat-card-icon {
  background: rgba(0, 180, 255, 0.12);
  color: #00b4ff;
}

.stat-card-lease .stat-card-icon {
  background: rgba(124, 58, 237, 0.12);
  color: #7c3aed;
}

.stat-card-total .stat-card-icon {
  background: linear-gradient(
    135deg,
    rgba(0, 180, 255, 0.15),
    rgba(124, 58, 237, 0.15)
  );
  color: #fff;
}

.stat-card-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  font-weight: 400;
  letter-spacing: 0.3px;
}

.stat-card-value {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  font-family: "SF Mono", "Courier New", monospace;
  margin-top: 1px;
}

.stat-card-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stat-ring {
  position: relative;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-ring svg {
  transform: rotate(-90deg);
}

.stat-ring-label {
  position: absolute;
  font-size: 10px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.7);
}

.stat-card-hash .stat-ring-label {
  color: #00b4ff;
}

.stat-card-lease .stat-ring-label {
  color: #7c3aed;
}

.stat-card-total {
  background: linear-gradient(
    135deg,
    rgba(0, 180, 255, 0.08),
    rgba(124, 58, 237, 0.08)
  );
  border-color: rgba(0, 180, 255, 0.12);
}

.stat-card-total .stat-card-value {
  font-size: 20px;
  background: linear-gradient(135deg, #00b4ff, #7c3aed);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.stat-total-badge {
  font-size: 13px;
  font-weight: 700;
  color: #00c951;
  background: rgba(0, 201, 81, 0.12);
  padding: 2px 10px;
  border-radius: 12px;
}

.stat-total-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.25);
}

/* ===== 响应式 ===== */
@media (max-width: 1024px) {
  .asset-overview-body {
    grid-template-columns: 1fr 1fr;
  }
  .asset-total-card {
    grid-column: 1 / -1;
  }
  .total-stats-new {
    grid-template-columns: 1fr 1fr;
  }
  .stat-card-total {
    grid-column: 1 / -1;
  }
}

@media (max-width: 768px) {
  .asset-overview-body {
    grid-template-columns: 1fr;
  }
  .asset-total-card {
    grid-column: 1;
  }
  .total-stats-new {
    grid-template-columns: 1fr;
  }
  .stat-card-total {
    grid-column: 1;
  }
  .asset-total-amount .amount-number {
    font-size: 24px;
  }
  .chart-container {
    height: 150px;
    min-height: 150px;
  }
  .chart-header {
    flex-wrap: wrap;
    gap: 8px;
  }
  .chart-header-left {
    flex-wrap: wrap;
    gap: 10px;
  }
}

@media (max-width: 480px) {
  .asset-overview {
    padding: 16px;
  }
  .asset-overview-header {
    flex-wrap: wrap;
    gap: 8px;
  }
  .asset-total-card {
    padding: 16px;
    flex-wrap: wrap;
  }
  .asset-total-amount .amount-number {
    font-size: 20px;
  }
  .asset-sub-card {
    padding: 14px 16px;
  }
  .sub-card-value {
    font-size: 16px;
  }
  .chart-container {
    height: 120px;
    min-height: 120px;
  }
  .stat-card {
    padding: 14px 16px;
  }
  .stat-card-value {
    font-size: 16px;
  }
  .stat-card-total .stat-card-value {
    font-size: 18px;
  }
  .stat-ring {
    width: 40px;
    height: 40px;
  }
  .stat-ring svg {
    width: 40px;
    height: 40px;
  }
  .time-tab {
    padding: 3px 10px;
    font-size: 11px;
  }
}
</style>
